import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ArrowRight,
  Package,
  Truck,
  Shield,
  Clock,
  BarChart3,
  Users,
  Globe,
  Headphones,
  CheckCircle,
  Box,
  Zap
} from "lucide-react";

const KMUFulfillmentPage = () => {
  const challenges = [
    {
      icon: Clock,
      title: "Zeitdruck im E-Commerce",
      description: "Online-Kunden erwarten schnelle Lieferung, lückenlose Sendungsverfolgung und einfache Retouren. Das erfordert durchdachte Logistikprozesse."
    },
    {
      icon: BarChart3,
      title: "Wettbewerbsvorteile sichern",
      description: "Mit professioneller Logistik und zuverlässigen Lieferzeiten heben Sie sich ab und binden Kunden langfristig an Ihr Unternehmen."
    },
    {
      icon: Globe,
      title: "Wachstum ermöglichen",
      description: "Ob regional oder überregional – mit dem richtigen Partner meistern Sie auch komplexe Lieferanforderungen und können Ihr Geschäft skalieren."
    }
  ];

  const features = [
    {
      icon: Package,
      label: "Shop-Anbindung",
      title: "Behalten Sie den Überblick über Ihre Bestellungen",
      description: "Wir bieten Ihnen flexible Anbindungsmöglichkeiten für Ihren Online-Shop. Bestelldaten werden automatisch übermittelt und verarbeitet. Mit unserer modernen Software haben Sie jederzeit Einblick in Bestände, Versandstatus und Retouren.",
      points: [
        "Einfache Shop-Integration",
        "Automatische Datenübertragung",
        "Echtzeit-Bestandsübersicht",
        "Transparente Auftragsverfolgung"
      ],
      imagePosition: "right"
    },
    {
      icon: Truck,
      label: "Effiziente Logistik",
      title: "Sparen Sie Zeit und Kosten durch optimierte Prozesse",
      description: "Logistikkosten sind ein wichtiger Faktor für kleine Unternehmen. Durch clevere Planung und gebündelte Transporte senken wir Ihre Kosten und setzen Ihre Ressourcen frei für das, was zählt: Ihr Kerngeschäft.",
      points: [
        "Optimierte Transportwege",
        "Kostentransparenz",
        "Flexible Abholzeiten",
        "Ressourcen für Ihr Wachstum"
      ],
      imagePosition: "left"
    },
    {
      icon: Shield,
      label: "Sichere Lagerung",
      title: "Ihre Produkte in besten Händen",
      description: "Profitieren Sie von unserer Erfahrung im sorgfältigen Umgang mit Waren aller Art. Unser geschultes Team sorgt für ordnungsgemäße Lagerung, regelmäßige Kontrollen und professionelles Handling.",
      points: [
        "Sorgfältige Warenlagerung",
        "Regelmäßige Qualitätskontrollen",
        "Geschultes Fachpersonal",
        "Sichere Lagerräume"
      ],
      imagePosition: "right"
    },
    {
      icon: Box,
      label: "Individuelle Verpackung",
      title: "Begeistern Sie Ihre Kunden beim Auspacken",
      description: "Der erste Eindruck zählt! Eine ansprechende Verpackung stärkt Ihre Marke und sorgt für zufriedene Kunden. Wir entwickeln mit Ihnen individuelle Verpackungslösungen, die Ihre Produkte optimal präsentieren.",
      points: [
        "Markengerechte Verpackungen",
        "Sichere Produktpolsterung",
        "Personalisierte Beilagen möglich",
        "Nachhaltige Materialien"
      ],
      imagePosition: "left"
    },
    {
      icon: Zap,
      label: "Schneller Versand",
      title: "Pünktliche Lieferung als Ihr Markenzeichen",
      description: "Ihre Kunden wollen ihre Bestellung schnell erhalten. Durch unseren Standort in Sulzbach und enge Zusammenarbeit mit Versanddienstleistern ermöglichen wir kurze Lieferzeiten und taggleichen Versand.",
      points: [
        "Taggleicher Versand möglich",
        "Zuverlässige Zustellung",
        "Retourenmanagement inklusive",
        "Flexible Versandoptionen"
      ],
      imagePosition: "right"
    },
    {
      icon: Users,
      label: "B2B & Endkunden",
      title: "Wir beliefern, wen Sie beliefern möchten",
      description: "Ob Direktversand an Ihre Endkunden oder Belieferung von Handelspartnern – wir passen uns Ihren Vertriebswegen an und sorgen für eine zuverlässige Zustellung.",
      points: [
        "Endkundenversand (D2C)",
        "Händlerbelieferung (B2B)",
        "Einhaltung von Liefervorgaben",
        "Flexible Mengen"
      ],
      imagePosition: "left"
    }
  ];

  const images = [
    "https://customer-assets.emergentagent.com/job_3f8853ef-18d6-476f-b97c-eda985cb2340/artifacts/egvlrw92_Ecommerce.jpg",
    "https://customer-assets.emergentagent.com/job_3f8853ef-18d6-476f-b97c-eda985cb2340/artifacts/z0lbohlr_Transporter.jpg",
    "https://customer-assets.emergentagent.com/job_3f8853ef-18d6-476f-b97c-eda985cb2340/artifacts/0q04sabn_Lagerregale.jpg",
    "https://customer-assets.emergentagent.com/job_3f8853ef-18d6-476f-b97c-eda985cb2340/artifacts/8wul2cb2_Verpackung_2.jpg",
    "https://customer-assets.emergentagent.com/job_3f8853ef-18d6-476f-b97c-eda985cb2340/artifacts/hvocsgmu_Zusteller_3.jpg",
    "https://customer-assets.emergentagent.com/job_3f8853ef-18d6-476f-b97c-eda985cb2340/artifacts/ed08gqs9_Luftfracht_2.jpg"
  ];

  return (
    <div data-testid="kmu-fulfillment-page">
      {/* Page Header */}
      <section className="page-header py-20 md:py-28 text-white">
        <div className="container mx-auto px-4 relative z-10">
          <span className="inline-block bg-orange-600 text-white text-sm uppercase tracking-widest font-bold px-4 py-2 mb-6">
            Für kleine Unternehmen
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Fulfillment für kleine Unternehmen
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl">
            Wir unterstützen Sie mit maßgeschneiderten Logistiklösungen – damit Sie sich auf Ihr Geschäft konzentrieren können.
          </p>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            {challenges.map((challenge, index) => {
              const Icon = challenge.icon;
              return (
                <div 
                  key={challenge.title}
                  className="text-center p-8 border border-slate-200 card-hover"
                  data-testid={`challenge-${index}`}
                >
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon className="text-orange-600 w-8 h-8" aria-hidden="true" />
                  </div>
                  <h3 className="font-bold text-xl mb-4 text-slate-900">{challenge.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{challenge.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Headline */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <span className="text-orange-600 uppercase tracking-widest font-bold text-sm">
              Fulfillment-Lösungen für KMU
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mt-4 mb-8 text-slate-900">
              Wachsen Sie mit einem zuverlässigen Partner an Ihrer Seite
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              Wir stellen uns <strong>individuell auf Ihre Bedürfnisse</strong> ein und entwickeln 
              gemeinsam mit Ihnen Lösungen, die Ihr Unternehmen voranbringen. Bei uns sind Sie 
              kein anonymer Kunde – Sie werden <strong>persönlich betreut</strong> von unserem 
              kompetenten Team, das jederzeit für Sie da ist.
            </p>
            <div className="bg-white p-8 border-l-4 border-orange-600 text-left">
              <p className="text-slate-700 italic text-lg">
                "Kleine und mittelständische Unternehmen brauchen einen Partner, der ihre 
                Herausforderungen versteht und flexibel reagieren kann. Genau das ist unser Anspruch."
              </p>
              <p className="text-slate-900 font-bold mt-4">– Waldemar Kauz, Geschäftsführer</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Sections */}
      {features.map((feature, index) => {
        const Icon = feature.icon;
        const isEven = index % 2 === 0;
        
        return (
          <section 
            key={feature.title}
            className={`py-20 ${isEven ? 'bg-white' : 'bg-slate-50'}`}
            data-testid={`feature-${index}`}
          >
            <div className="container mx-auto px-4">
              <div className={`grid lg:grid-cols-2 gap-16 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
                <div className={isEven ? 'lg:order-1' : 'lg:order-2'}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-orange-600 flex items-center justify-center">
                      <Icon className="text-white w-6 h-6" aria-hidden="true" />
                    </div>
                    <span className="text-orange-600 uppercase tracking-widest font-bold text-sm">
                      {feature.label}
                    </span>
                  </div>
                  
                  <h2 className="text-2xl md:text-3xl font-bold mb-6 text-slate-900">
                    {feature.title}
                  </h2>
                  
                  <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                    {feature.description}
                  </p>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    {feature.points.map((point, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <CheckCircle 
                          className="text-orange-600 flex-shrink-0 mt-0.5" 
                          size={20} 
                          aria-hidden="true" 
                        />
                        <span className="text-slate-700">{point}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className={isEven ? 'lg:order-2' : 'lg:order-1'}>
                  <img
                    src={images[index % images.length]}
                    alt={feature.title}
                    className="w-full shadow-xl"
                  />
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Customer Support Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-orange-600 flex items-center justify-center">
                  <Headphones className="text-white w-6 h-6" aria-hidden="true" />
                </div>
                <span className="text-orange-500 uppercase tracking-widest font-bold text-sm">
                  Persönlicher Service
                </span>
              </div>
              
              <h2 className="text-2xl md:text-3xl font-bold mb-6">
                Wir sind für Sie da – auch nach dem Versand
              </h2>
              
              <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                Guter Service endet nicht mit dem Paketversand. Wir unterstützen Sie auch 
                bei Kundenanfragen und sorgen für eine schnelle Lösung von Problemen.
              </p>
              
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                Durch die enge Verzahnung von Fulfillment und Kundenservice können wir 
                schnell und kompetent reagieren – für zufriedene Kunden und eine starke 
                Kundenbindung.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  "Persönliche Ansprechpartner",
                  "Schnelle Problemlösung",
                  "Alles aus einer Hand",
                  "Fokus auf Ihr Kerngeschäft"
                ].map((point, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle 
                      className="text-orange-500 flex-shrink-0 mt-0.5" 
                      size={20} 
                      aria-hidden="true" 
                    />
                    <span className="text-slate-300">{point}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="text-center">
              <img
                src="https://customer-assets.emergentagent.com/job_3f8853ef-18d6-476f-b97c-eda985cb2340/artifacts/r4peruyx_Verpackung_1.jpg"
                alt="Kundenservice"
                className="w-full shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Kauz Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-orange-600 uppercase tracking-widest font-bold text-sm">
              Unsere Stärken
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4 text-slate-900">
              Über 20 Jahre Erfahrung im Fulfillment
            </h2>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <p className="text-xl text-slate-600 text-center leading-relaxed mb-12">
              Als <strong>Full-Service-Logistikpartner</strong> mit moderner Technik und 
              flexiblen Lösungen betreuen wir Unternehmen aus verschiedensten Branchen. 
              Wir verstehen die besonderen Anforderungen kleiner Unternehmen und bieten 
              Ihnen maßgeschneiderte Unterstützung.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { number: "20+", label: "Jahre Erfahrung" },
                { number: "500+", label: "Zufriedene Kunden" },
                { number: "100%", label: "Einsatz für Sie" }
              ].map((stat, index) => (
                <div key={index} className="text-center p-6 bg-slate-50">
                  <div className="mono text-4xl font-bold text-orange-600 mb-2">{stat.number}</div>
                  <div className="text-slate-600 uppercase tracking-wider text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-orange-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Haben wir Ihr Interesse geweckt?
          </h2>
          <p className="text-xl text-orange-100 mb-10 max-w-2xl mx-auto">
            Nehmen Sie Kontakt zu uns auf! Wir besprechen gerne Ihre Anforderungen 
            und erstellen Ihnen ein individuelles Angebot.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/angebot">
              <Button 
                className="bg-white text-orange-600 hover:bg-slate-100 uppercase tracking-wider font-bold px-10 py-6 text-lg"
                data-testid="kmu-cta-quote"
              >
                Angebot anfordern
              </Button>
            </Link>
            <Link to="/kontakt">
              <Button 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-orange-600 uppercase tracking-wider font-bold px-10 py-6 text-lg bg-transparent"
                data-testid="kmu-cta-contact"
              >
                Kontakt aufnehmen
                <ArrowRight className="ml-2" size={20} aria-hidden="true" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default KMUFulfillmentPage;
